"use client";

import Link from "next/link";
import { CheckCircle2, Zap, Shield, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface Integration {
  name: string;
  category: string;
  type: "api" | "xml" | "csv" | "webhook";
  desc: string;
}

export default function IntegrationsProductPage() {
  const { t } = useI18n();

  const integrations: Integration[] = [
    // PMS
    { name: "Opera Cloud", category: "pms", type: "api", desc: "Synxis / Oracle" },
    { name: "Fidelio", category: "pms", type: "api", desc: "Micros / Oracle legacy" },
    { name: "Logus HMS", category: "pms", type: "api", desc: "Russian PMS leader" },
    { name: "TravelLine", category: "pms", type: "api", desc: "CRS + PMS" },
    { name: "Mews", category: "pms", type: "api", desc: "Cloud-native" },
    { name: "1C:Hotel", category: "pms", type: "xml", desc: "1C ecosystem" },

    // POS / F&B
    { name: "iiko", category: "pos", type: "api", desc: "Russian F&B leader" },
    { name: "R-Keeper 7", category: "pos", type: "api", desc: "POS + KDS" },
    { name: "Toast", category: "pos", type: "api", desc: "Cloud POS" },
    { name: "TouchBistro", category: "pos", type: "api", desc: "iPad POS" },

    // Channel Managers
    { name: "Booking.com", category: "channel", type: "api", desc: "OTA sync" },
    { name: "Expedia", category: "channel", type: "api", desc: "OTA sync" },
    { name: "Airbnb", category: "channel", type: "api", desc: "OTA sync" },
    { name: "Kiwi.com", category: "channel", type: "api", desc: "B2B channel" },

    // Accounting
    { name: "1C:Accounting", category: "accounting", type: "xml", desc: "General ledger sync" },
    { name: "1C:Payroll", category: "accounting", type: "xml", desc: "Salary integration" },
    { name: "SAP", category: "accounting", type: "api", desc: "Enterprise ERP" },
    { name: "NetSuite", category: "accounting", type: "api", desc: "Cloud ERP" },

    // SPA / Activity
    { name: "TNG SPA", category: "spa", type: "api", desc: "Russian SPA ecosystem" },
    { name: "Mindbody", category: "spa", type: "api", desc: "Global SPA platform" },
    { name: "Mariana Tek", category: "spa", type: "api", desc: "Wellness management" },

    // Revenue
    { name: "Revinate", category: "revenue", type: "api", desc: "Guest insights" },
    { name: "Looker", category: "revenue", type: "api", desc: "BI analytics" },
    { name: "Tableau", category: "revenue", type: "api", desc: "Data visualization" },

    // HR
    { name: "Kronos", category: "hr", type: "api", desc: "Workforce management" },
    { name: "Workday", category: "hr", type: "api", desc: "HCM platform" },
  ];

  const categories = [
    {
      key: "pms",
      title: t("pages.product.integrations.category.pms") || "PMS Systems",
      subtitle: t("pages.product.integrations.category.pmsDesc") || "Check-in, stay, guest data",
      icon: "🏨",
    },
    {
      key: "pos",
      title: t("pages.product.integrations.category.pos") || "POS / F&B",
      subtitle: t("pages.product.integrations.category.posDesc") || "Restaurant, bar, café revenue",
      icon: "🍽️",
    },
    {
      key: "channel",
      title: t("pages.product.integrations.category.channel") || "Channel Managers",
      subtitle: t("pages.product.integrations.category.channelDesc") || "OTA, B2B channels, rate sync",
      icon: "🌐",
    },
    {
      key: "accounting",
      title: t("pages.product.integrations.category.accounting") || "Accounting",
      subtitle: t("pages.product.integrations.category.accountingDesc") || "GL, payroll, invoices",
      icon: "📊",
    },
    {
      key: "spa",
      title: t("pages.product.integrations.category.spa") || "SPA / Wellness",
      subtitle: t("pages.product.integrations.category.spaDesc") || "Spa, activities, bookings",
      icon: "🧘",
    },
    {
      key: "revenue",
      title: t("pages.product.integrations.category.revenue") || "Revenue & BI",
      subtitle: t("pages.product.integrations.category.revenueDesc") || "Analytics, reporting, insights",
      icon: "📈",
    },
    {
      key: "hr",
      title: t("pages.product.integrations.category.hr") || "HR & Payroll",
      subtitle: t("pages.product.integrations.category.hrDesc") || "Timekeeping, wages, compliance",
      icon: "👥",
    },
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case "api":
        return "bg-blue-50 border-blue-200";
      case "xml":
        return "bg-green-50 border-green-200";
      case "webhook":
        return "bg-purple-50 border-purple-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "api":
        return "REST API";
      case "xml":
        return "XML/OData";
      case "webhook":
        return "Webhook";
      default:
        return "CSV Import";
    }
  };

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            {t("pages.product.integrations.headline") ||
              "30+ интеграций. Подключение за дни."}
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            {t("pages.product.integrations.description") ||
              `Keiront синхронизируется с вашими системами:
              PMS, POS, бухгалтерией, каналами продаж и HR.
              No migration. No duplicate entry.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild variant="default" className="h-12">
              <Link href="/contact">
                {t("pages.product.integrations.cta") || "Запросить интеграцию"}
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-12">
              <Link href="/contact">
                {t("pages.product.integrations.notFound") || "Не нашли свою систему?"}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.integrations.categoriesTitle") ||
            "По категориям"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-cols-4 gap-6">
          {categories.map((cat, idx) => {
            const count = integrations.filter((i) => i.category === cat.key).length;
            return (
              <div
                key={idx}
                className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors cursor-pointer"
              >
                <div className="text-4xl mb-3">{cat.icon}</div>
                <h3 className="font-bold text-slate-900 mb-1">{cat.title}</h3>
                <p className="text-sm text-slate-600 mb-3">{cat.subtitle}</p>
                <p className="text-sm font-semibold text-keiro-green">
                  {count} {count === 1 ? "system" : "systems"}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* All Integrations List */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.integrations.allTitle") ||
            "Все интеграции"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((integration, idx) => (
            <div
              key={idx}
              className={`border rounded-lg p-4 ${getTypeColor(integration.type)}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-slate-900">{integration.name}</h3>
                  <p className="text-sm text-slate-600">{integration.desc}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600 uppercase">
                  {getTypeLabel(integration.type)}
                </span>
                <CheckCircle2 size={16} className="text-keiro-green" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Connection Methods */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.integrations.methodsTitle") ||
            "Способы подключения"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            {
              type: "api",
              title: t("pages.product.integrations.method.api.title") || "REST API",
              desc: t("pages.product.integrations.method.api.desc") || "Real-time sync, webhooks, OAuth 2.0",
            },
            {
              type: "xml",
              title: t("pages.product.integrations.method.xml.title") || "XML / OData",
              desc: t("pages.product.integrations.method.xml.desc") || "Standard format, 1C integration",
            },
            {
              type: "webhook",
              title: t("pages.product.integrations.method.webhook.title") || "Webhook",
              desc: t("pages.product.integrations.method.webhook.desc") || "Event-driven updates, instant",
            },
            {
              type: "csv",
              title: t("pages.product.integrations.method.csv.title") || "CSV / Excel",
              desc: t("pages.product.integrations.method.csv.desc") || "Batch import, historical data",
            },
          ].map((method, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6 text-center">
              <Zap size={28} className="text-keiro-green mx-auto mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">{method.title}</h3>
              <p className="text-sm text-slate-600">{method.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.integrations.timelineTitle") ||
            "Это происходит быстро"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            {
              day: "День 1",
              title: t("pages.product.integrations.timeline.day1.title") || "Setup",
              desc: t("pages.product.integrations.timeline.day1.desc") || "API keys, credentials, test connection",
            },
            {
              day: "День 2-3",
              title: t("pages.product.integrations.timeline.day2.title") || "Mapping",
              desc: t("pages.product.integrations.timeline.day2.desc") || "Field mapping, account structure",
            },
            {
              day: "День 4-5",
              title: t("pages.product.integrations.timeline.day3.title") || "Testing",
              desc: t("pages.product.integrations.timeline.day3.desc") || "Data validation, reconciliation",
            },
            {
              day: "День 6",
              title: t("pages.product.integrations.timeline.day4.title") || "Go Live",
              desc: t("pages.product.integrations.timeline.day4.desc") || "Production sync, monitoring",
            },
          ].map((item, idx) => (
            <div key={idx} className="bg-keiro-mist border border-keiro-green/20 rounded-xl p-6 text-center">
              <p className="text-lg font-bold text-keiro-green mb-2">{item.day}</p>
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Security & Data */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.integrations.securityTitle") ||
            "Безопасность и соответствие"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.integrations.security.encryption.title") ||
                "Encrypted in Transit",
              desc: t("pages.product.integrations.security.encryption.desc") ||
                "TLS 1.3, all APIs over HTTPS only",
            },
            {
              title: t("pages.product.integrations.security.isolation.title") ||
                "Data Isolation",
              desc: t("pages.product.integrations.security.isolation.desc") ||
                "Schema-per-tenant, no cross-tenant leaks",
            },
            {
              title: t("pages.product.integrations.security.audit.title") ||
                "Audit Trail",
              desc: t("pages.product.integrations.security.audit.desc") ||
                "All sync operations logged, compliance-ready",
            },
          ].map((item, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <Shield size={28} className="text-keiro-green mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Support & Pricing */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.integrations.supportTitle") ||
            "Команда готовит вас"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.integrations.support.setup.title") ||
                "Integration Setup",
              desc: t("pages.product.integrations.support.setup.desc") ||
                "Our team handles API config & security",
            },
            {
              title: t("pages.product.integrations.support.training.title") ||
                "Team Training",
              desc: t("pages.product.integrations.support.training.desc") ||
                "1-hour session on data flows & troubleshooting",
            },
            {
              title: t("pages.product.integrations.support.monitoring.title") ||
                "Ongoing Monitoring",
              desc: t("pages.product.integrations.support.monitoring.desc") ||
                "24/5 support, real-time alerts if sync fails",
            },
          ].map((item, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <Users size={28} className="text-keiro-green mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Custom Integration */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="bg-keiro-mist rounded-xl p-12 border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            {t("pages.product.integrations.customTitle") ||
              "Не нашли свою систему?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8 text-center">
            {t("pages.product.integrations.customDesc") ||
              "Мы подключаем любые системы через REST API, OData или другие стандарты. Напишите нам – разберёмся за день."}
          </p>
          <div className="flex justify-center">
            <Button asChild variant="default" className="h-12 px-8">
              <Link href="/contact">
                {t("pages.product.integrations.customCta") ||
                  "Обсудить интеграцию"}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.product.integrations.ctaTitle") ||
              "Готовы запустить интеграцию?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.product.integrations.ctaDesc") ||
              "14 дней бесплатного доступа, включая setup"}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.product.integrations.ctaButton") ||
                "Запросить демо"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
